'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const channels = [
  { icon: 'ChatBubbleLeftRightIcon', label: 'SMS' },
  { icon: 'DevicePhoneMobileIcon', label: 'WhatsApp' },
  { icon: 'SparklesIcon', label: 'RCS' },
  { icon: 'CpuChipIcon', label: 'Bot Flows' },
  { icon: 'Squares2X2Icon', label: 'Campaigns' },
  { icon: 'CodeBracketIcon', label: 'Webhooks / API' },
];

const stats = [
  { value: '4 Channels', label: 'SMS, WhatsApp, RCS & Bot', icon: 'Squares2X2Icon' },
  { value: 'DLT', label: 'Registered Sender IDs & Templates', icon: 'ShieldCheckIcon' },
  { value: 'Real-time', label: 'Delivery Reports & Webhooks', icon: 'BoltIcon' },
  { value: 'Visual', label: 'Drag & Drop Bot Builder', icon: 'CpuChipIcon' },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'ChatBubbleLeftRightIcon',
    label: 'Bulk SMS Gateway with DLT Compliance',
    desc: 'Send transactional and promotional SMS at scale with registered Sender IDs, DLT-approved templates, blacklist management, and full API-fail and webhook logs for every message.',
    pills: ['DLT Sender IDs', 'Template approval', 'Blacklist control', 'Delivery logs'],
  },
  {
    num: '02',
    icon: 'DevicePhoneMobileIcon',
    label: 'WhatsApp Business API',
    desc: 'Run broadcasts, manage approved message templates, handle a shared team inbox, and track number-wise analytics — all connected directly through Meta\'s official WhatsApp Business platform.',
    pills: ['Broadcast messaging', 'Template manager', 'Shared inbox', 'Number analytics'],
  },
  {
    num: '03',
    icon: 'CpuChipIcon',
    label: 'Visual Bot & Flow Builder',
    desc: 'Design conversational journeys on a drag-and-drop canvas — no code needed. Build decision trees, auto-replies, and hand-offs to a live agent for SMS and WhatsApp conversations.',
    pills: ['Drag & drop canvas', 'Conditional logic', 'Live-agent hand-off', 'Reusable flows'],
  },
  {
    num: '04',
    icon: 'SparklesIcon',
    label: 'RCS Rich Messaging',
    desc: 'Send rich, branded messages with images, carousels, and quick-reply buttons through Rich Communication Services — a modern upgrade over plain SMS for richer customer engagement.',
    pills: ['Rich cards', 'Quick replies', 'Branded sender', 'Carousels'],
  },
  {
    num: '05',
    icon: 'UserGroupIcon',
    label: 'Unified Contacts & Campaigns',
    desc: 'Import, segment, and manage contact lists once, then launch coordinated campaigns across SMS, WhatsApp, and RCS from a single campaign manager with scheduling and audience targeting.',
    pills: ['Contact segmentation', 'Multi-channel campaigns', 'Scheduling', 'Audience targeting'],
  },
  {
    num: '06',
    icon: 'ChartBarIcon',
    label: 'Delivery Reports & Analytics',
    desc: 'Track delivery, read, and reply rates in real time. Export reports, monitor API failures, and get instant webhook notifications for every event across every channel.',
    pills: ['Real-time reports', 'Read receipts', 'Webhook events', 'Exportable data'],
  },
];

const workflowSteps = [
  { icon: 'CpuChipIcon', title: 'Build a Flow', desc: 'Design the conversation on the visual bot canvas — no code needed.' },
  { icon: 'UserGroupIcon', title: 'Import Contacts', desc: 'Upload or sync your audience and segment them by tags or attributes.' },
  { icon: 'Squares2X2Icon', title: 'Launch Campaign', desc: 'Send via SMS, WhatsApp, or RCS — scheduled or instant.' },
  { icon: 'ChatBubbleLeftRightIcon', title: 'Customer Replies', desc: 'The bot responds instantly, or hands off to a live agent inbox.' },
  { icon: 'BellAlertIcon', title: 'Webhook Fires', desc: 'Delivery, read, and reply events push to your systems in real time.' },
  { icon: 'ChartBarIcon', title: 'Track & Optimise', desc: 'Review delivery reports and refine templates and flows.' },
];

const marketingFeatures = [
  'Multi-channel Campaign Manager',
  'DLT-approved SMS Templates',
  'WhatsApp Broadcast & Template Library',
  'Contact Import & Segmentation',
  'Scheduled & Triggered Campaigns',
  'Blacklist & Opt-out Handling',
  'Shared Team Inbox for Replies',
  'Delivery & Read Rate Reports',
];

const developerFeatures = [
  'REST API for SMS, WhatsApp & RCS',
  'Real-time Webhooks for every event',
  'API Keys & Access Management',
  'Meta / WhatsApp Business Integration',
  'Sandbox for API-fail Debugging',
  'Bot Canvas — Custom Node Types',
  'Role-based Team & Branch Access',
  'Usage & Billing Dashboard',
];

const managementFeatures = [
  { icon: 'ShieldCheckIcon', label: 'DLT & Sender ID Management', sub: 'Register senders, approve templates' },
  { icon: 'DocumentTextIcon', label: 'Template Library', sub: 'Unified templates across all channels' },
  { icon: 'UserGroupIcon', label: 'Contacts & Segments', sub: 'Import, tag, and target audiences' },
  { icon: 'BellAlertIcon', label: 'Webhook Logs & API Failures', sub: 'Debug every delivery event' },
  { icon: 'BuildingStorefrontIcon', label: 'Multi-branch / Team Access', sub: 'Role-based users across branches' },
  { icon: 'ChartBarIcon', label: 'Campaign & Delivery Reports', sub: 'Exportable, real-time analytics' },
  { icon: 'KeyIcon', label: 'API Keys & Integrations', sub: 'Connect Meta, CRMs & internal tools' },
  { icon: 'CurrencyRupeeIcon', label: 'Usage & Billing', sub: 'Per-channel usage and invoicing' },
];

const inboxFeatures = [
  'One inbox for SMS, WhatsApp & RCS replies',
  'Bot handles FAQs, agent handles the rest',
  'Conversation history per contact',
  'Team assignment & internal notes',
  'Quick replies & canned responses',
  'Read receipts across channels',
  'Escalation rules to human agents',
  'Search across all conversations',
];

export default function EllanCpaasContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (violet gradient + channel grid) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 55%, #3B82F6 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 65%)', top: '-200px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.6) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/60 text-sm font-medium mb-7 hover:text-white transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-5">
                <Icon name="ChatBubbleLeftRightIcon" size={11} />
                Communications Platform
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Ellan CPaaS —{' '}
                <span className="italic font-light" style={{ color: '#BFDBFE' }}>
                  One Platform,
                </span>
                <br />Every Conversation.
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/65 text-lg leading-relaxed mb-9 max-w-lg">
                SMS, WhatsApp Business API, RCS, and a visual chatbot builder — unified in one dashboard, with DLT compliance, campaign management, and real-time delivery analytics.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/25 hover:border-white/50 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['DLT compliant', 'Multi-channel inbox', 'No-code bot builder', 'Real-time webhooks'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/50 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#BFDBFE]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — channel grid */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl p-6 border border-white/15"
                style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
              >
                <p className="text-[11px] font-semibold text-white/40 uppercase tracking-widest mb-4">All Channels, One Dashboard</p>
                <div className="grid grid-cols-3 gap-3">
                  {channels.map((s) => (
                    <div
                      key={s.label}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl border border-white/10 bg-white/[0.06] hover:bg-white/[0.12] transition-colors text-center group"
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/15 group-hover:scale-110 transition-transform">
                        <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={17} className="text-white" />
                      </div>
                      <span className="text-[11px] font-semibold text-white/80 leading-tight">{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-white/[0.06] border border-white/10">
                  <Icon name="PlusCircleIcon" size={16} className="text-[#BFDBFE] flex-shrink-0" />
                  <span className="text-xs text-white/60 font-medium">Plug in via API — connect any of your own systems</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/60 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES ── */}
      <div className="py-24" style={{ background: '#EFF6FF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(29,78,216,0.3)] bg-[rgba(29,78,216,0.08)] text-[11px] font-semibold tracking-widest text-[#1D4ED8] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that power{' '}
              <span className="italic font-light text-[#1D4ED8]">every customer conversation.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From bulk SMS to rich WhatsApp broadcasts and no-code bots — all from one unified platform.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border hover:shadow-lg transition-all duration-300`}
                style={{
                  background: 'white',
                  borderColor: i % 2 === 0 ? 'rgba(29,78,216,0.15)' : 'rgba(15,23,42,0.07)',
                }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <span
                    className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #1D4ED8, #2563EB)' }}
                  >
                    {f.num}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(29,78,216,0.1)' }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#1D4ED8]" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug pt-1 flex-1">{f.label}</h3>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{f.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {f.pills.map((p) => (
                    <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#1D4ED8]" style={{ background: 'rgba(29,78,216,0.08)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
{/* ── WORKFLOW ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#021A5E' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(191,219,254,0.35) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#BFDBFE]/30 bg-[#BFDBFE]/10 text-[11px] font-semibold tracking-widest text-[#BFDBFE] uppercase mb-5">
              Campaign Workflow
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              Build once,{' '}
              <span className="italic font-light text-[#BFDBFE]">send everywhere.</span>
            </h2>
          </div>

          <div className="relative grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="hidden lg:block absolute top-[26px] left-[calc(8.33%+20px)] right-[calc(8.33%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(221,214,254,0.05), rgba(221,214,254,0.3) 20%, rgba(221,214,254,0.3) 80%, rgba(221,214,254,0.05))' }} />

            {workflowSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #1D4ED8, #2563EB)', boxShadow: '0 0 24px rgba(29,78,216,0.4)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
<div className="text-[10px] font-bold text-[#BFDBFE]/70 tracking-widest uppercase mb-1.5">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-1.5">{step.title}</h3>
                <p className="text-xs text-white/70 leading-relaxed">{step.desc}</p>              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 mt-14 flex flex-wrap justify-center gap-3">
            {['DLT-verified Sending', 'Live Delivery Status', 'Bot Auto-reply', 'Agent Hand-off', 'Webhook Events', 'Exportable Reports'].map((p) => (
<div key={p} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#BFDBFE]/25 bg-white/[0.08]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#BFDBFE]" />
                <span className="text-sm text-white/85 font-medium">{p}</span>              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MARKETING & DEVELOPER (2-col) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(29,78,216,0.3)] bg-[rgba(29,78,216,0.08)] text-[11px] font-semibold tracking-widest text-[#1D4ED8] uppercase mb-4">
              Built for Both
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              For Marketing teams &{' '}
              <span className="italic font-light text-[#1D4ED8]">for Developers.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Marketing */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(29,78,216,0.15)] shadow-sm">
              <div className="p-6" style={{ background: 'linear-gradient(135deg, #1D4ED8, #2563EB)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="MegaphoneIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">For Marketing Teams</h3>
                    <p className="text-white/60 text-xs">Campaigns, templates & reports</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2" style={{ background: '#EFF6FF' }}>
                {marketingFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-[rgba(29,78,216,0.08)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(29,78,216,0.12)' }}>
                      <Icon name="CheckIcon" size={10} className="text-[#1D4ED8]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Developer */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm">
              <div className="p-6 bg-[#0F172A]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon name="CodeBracketIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">For Developers</h3>
                    <p className="text-white/50 text-xs">API keys, webhooks & integrations</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#F5F5F5]">
                {developerFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-3 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={10} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MANAGEMENT DASHBOARD ── */}
      <div className="py-24" style={{ background: '#EFF6FF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(29,78,216,0.3)] bg-[rgba(29,78,216,0.08)] text-[11px] font-semibold tracking-widest text-[#1D4ED8] uppercase mb-4">
              Operations Control
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Every channel.{' '}
              <span className="italic font-light text-[#1D4ED8]">One dashboard.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">From DLT registration to API keys and billing — your admin panel handles it all.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {managementFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group p-6 rounded-2xl bg-white border border-[rgba(29,78,216,0.1)] hover:border-[rgba(29,78,216,0.3)] hover:shadow-md transition-all duration-300`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: 'rgba(29,78,216,0.1)' }}
                >
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#1D4ED8]" />
                </div>
                <p className="font-semibold text-sm text-[#0F172A] mb-1.5">{f.label}</p>
                <p className="text-xs text-[#64748B] leading-relaxed">{f.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── UNIFIED INBOX ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(29,78,216,0.3)] bg-[rgba(29,78,216,0.08)] text-[11px] font-semibold tracking-widest text-[#1D4ED8] uppercase mb-5">
                Unified Inbox
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Every reply,{' '}
                <span className="italic font-light text-[#1D4ED8]">in one place.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Whether a customer replies over SMS, WhatsApp, or RCS, your team sees it in one shared inbox — with the bot handling routine questions and agents stepping in for the rest.
              </p>
              <div className="flex flex-col gap-3">
                {inboxFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #1D4ED8, #2563EB)' }}
                    >
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — inbox visual */}
            <div className="animate-on-scroll stagger-2 flex flex-col gap-4">
              <div
                className="rounded-2xl p-6 text-white text-center"
                style={{ background: 'linear-gradient(135deg, #0F172A, #1D4ED8)' }}
              >
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <Icon name="InboxIcon" size={22} className="text-white" />
                </div>
                <p className="font-semibold text-base">Team Inbox</p>
                <p className="text-white/60 text-xs mt-1">Every SMS, WhatsApp & RCS conversation, unified</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {['SMS', 'WhatsApp', 'RCS'].map((b) => (
                  <div key={b} className="rounded-xl p-4 text-center border" style={{ background: '#EFF6FF', borderColor: 'rgba(29,78,216,0.2)' }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ background: 'rgba(29,78,216,0.12)' }}>
                      <Icon name="ChatBubbleLeftRightIcon" size={16} className="text-[#1D4ED8]" />
                    </div>
                    <p className="text-xs font-semibold text-[#0F172A] leading-snug">{b}</p>
                    <p className="text-[10px] text-[#64748B] mt-0.5">Bot + Agent</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'EyeIcon', title: 'Unified Visibility', desc: 'All channels, one screen' },
                  { icon: 'AdjustmentsHorizontalIcon', title: 'Escalation Rules', desc: 'Auto-route to the right agent' },
                ].map((c) => (
                  <div key={c.title} className="rounded-xl p-5 border" style={{ borderColor: 'rgba(29,78,216,0.15)', background: 'rgba(29,78,216,0.04)' }}>
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#1D4ED8] mb-2" />
                    <p className="font-semibold text-sm text-[#0F172A] mb-0.5">{c.title}</p>
                    <p className="text-xs text-[#475569]">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

{/* ── CTA ── */}
      <div className="py-24" style={{ background: '#EFF6FF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden border"
            style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 55%, #3B82F6 100%)', borderColor: 'rgba(29,78,216,0.15)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              One platform for{' '}
              <span className="italic font-light text-[#BFDBFE]">every conversation.</span>
            </h2>

            <p className="relative text-white/65 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/70 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#1D4ED8] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/65 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}